// 用户身份类型
export type UserRole = '爸爸' | '妈妈' | '祖辈' | '其他照护者';

// 宝宝信息
export interface BabyInfo {
  birthDate?: Date; // 已出生
  dueDate?: Date;   // 预产期
}

// 阶段枚举
export enum Stage {
  NEWBORN = '新生儿期 0-28 天',
  ONE_TO_THREE = '1-3 个月',
  FOUR_TO_SIX = '4-6 个月',
  SEVEN_TO_TWELVE = '7-12 个月'
}

// 模块枚举
export enum Module {
  FEEDING = '喂养',
  SLEEP = '睡眠',
  DAILY_CARE = '日常护理',
  SAFETY = '安全防护',
  DEVELOPMENT = '发育观察'
}

// 关卡状态
export enum LevelStatus {
  NOT_STARTED = '未开始',
  IN_PROGRESS = '进行中',
  COMPLETED = '已完成',
  NEED_REVIEW = '待复习',
  LOCKED = '锁定'
}

// 关卡
export interface Level {
  id: string;
  stage: Stage;
  module: Module;
  title: string;
  description: string;
  estimatedTime: number; // 分钟
  status: LevelStatus;
  order: number;
  /** 本关知识点列表，展示在关卡详情页 */
  knowledgePoints: string[];
  /** 风险边界提示，告知用户什么情况需要就医 */
  riskWarning?: string;
}

// 题目类型
export type QuestionType = 'single' | 'multiple' | 'truefalse' | 'scenario';

// 题目
export interface Question {
  id: string;
  levelId: string;
  type: QuestionType;
  text: string;
  options?: string[];
  correctAnswer: string | string[];
  explanation: string;
  /** 可选：限定可见角色。不填则所有角色均可见 */
  roles?: UserRole[];
}

// 学习记录
export interface LearningRecord {
  levelId: string;
  score: number;
  correctCount: number;
  totalCount: number;
  completedAt: Date;
  wrongQuestionIds: string[];
}

// ========== 趣味性系统 ==========

// 称号系统
export enum Title {
  NEW_PARENT = '🌱 新手爸妈',   // 0-5 星
  SCHOLAR = '📚 育儿学者',      // 6-15 星
  EXPERT = '🛡️ 育儿达人',      // 16-30 星
  MASTER = '👑 育儿大师',       // 30+ 星
}

// 称号星数阈值
export const TITLE_THRESHOLDS: { title: Title; minStars: number }[] = [
  { title: Title.MASTER, minStars: 30 },
  { title: Title.EXPERT, minStars: 16 },
  { title: Title.SCHOLAR, minStars: 6 },
  { title: Title.NEW_PARENT, minStars: 0 },
];

// 连胜记录
export interface StreakRecord {
  currentStreak: number;         // 当前连续学习天数
  longestStreak: number;         // 历史最长连胜
  lastStudyDate: string;         // 最后学习日期 'YYYY-MM-DD'
  streakMilestones: number[];    // 已达成的连胜里程碑 [3, 7, 14, 30]
}

// 每日推荐
export interface DailyRecommend {
  date: string;                  // 'YYYY-MM-DD'
  recommendedLevelId: string;    // 推荐关卡 ID
  completed: boolean;            // 当天是否完成
}

// 错题挑战记录
export interface ChallengeRecord {
  lastChallengeDate: string;     // 'YYYY-MM-DD'
  challengePassed: boolean;      // 是否全部答对
  challengeScore: number;        // 得分
}

// 模块掌握度（用于雷达图）
export interface ModuleMastery {
  module: Module;
  accuracy: number;              // 0-100
  completedLevels: number;
  totalLevels: number;
}

// ========== 成就徽章系统 ==========

export enum BadgeCategory {
  LEARNING = '学习成就',
  PERSISTENCE = '坚持成就',
  CHALLENGE = '挑战成就',
  REVIEW = '复习成就',
  EXPLORATION = '探索成就',
  SPECIAL = '特殊行为',
  HIDDEN = '隐藏成就',
}

export enum BadgeDifficulty {
  EASY = 1,      // ⭐
  MEDIUM = 2,    // ⭐⭐
  HARD = 3,      // ⭐⭐⭐
}

export interface BadgeDefinition {
  id: string;
  name: string;
  icon: string;
  description: string;           // 解锁条件描述（隐藏成就显示为 "???"）
  category: BadgeCategory;
  difficulty: BadgeDifficulty;
  isHidden: boolean;             // 隐藏成就：解锁前不显示名称和条件
  /** 判定函数：传入 store 快照，返回是否已解锁 */
  unlocked: (snap: BadgeSnapshot) => boolean;
}

/** 传入判定函数的 store 数据快照 */
export interface BadgeSnapshot {
  completedLevels: {
    levelId: string;
    correctCount: number;
    totalCount: number;
    timestamp: number;
  }[];
  wrongQuestions: {
    questionId: string;
    levelId: string;
    retried: boolean;
    retryCorrect: boolean;
    timestamp: number;
    retryCount?: number;
  }[];
  reviewCount: number;
  streak: {
    currentStreak: number;
    longestStreak: number;
    lastStudyDate: string;
    streakMilestones: number[];
  };
  stageChallengeRecords: Record<string, {
    lastChallengeDate: string;
    challengePassed: boolean;
    challengeScore: number;
  }>;
  bonusStars: number;
  dailyFirstWinDate: string;
  userRole: string | null;
  babyBorn: boolean;
  babyDate: string;
  dueDate: string;
  onboardingComplete: boolean;
  // 徽章辅助追踪字段
  usedRoles: string[];
  preBirthLearning: boolean;
  totalChallenges: number;
  fastestChallengeSeconds: number;
  firstWinDates: string[];
  homeVisits: number;
  questionTypes: string[];
  firstModuleCompleted: string;
  stageCompleted: string[];
}

/** 本地持久化的额外徽章追踪数据 */
export interface BadgeAuxData {
  usedRoles: string[];
  preBirthLearning: boolean;
  totalChallenges: number;
  fastestChallengeSeconds: number;
  firstWinDates: string[];
  homeVisits: number;
  questionTypes: string[];
  firstModuleCompleted: string;
  /** 已完成阶段的名称列表，用于阶段完成仪式 */
  stageCompleted: string[];
}